
import {
  defineComponent, onBeforeMount, reactive,
} from 'vue';
import { useAppBase } from '@/core/composables/AppBase';
import { IUsuario, IUsuarioPrivilegio } from '@/models/Entidades/IUsuario';
import Card from '@/core/components/UI/Card.vue';
import Icone from '@/core/components/UI/Icone.vue';
import MensagemSemDados from '@/core/components/UI/MensagemSemDados.vue';
import ComunicacaoApi from '@/core/components/UI/ComunicacaoApi.vue';
import CampoTelefone from '@/core/components/UI/CampoTelefone.vue';
import CampoEmail from '@/core/components/UI/CampoEmail.vue';
import CampoSenha from '@/core/components/UI/CampoSenha.vue';
import SelecionarSimNao from '@/core/components/UI/SelecionarSimNao.vue';
import SelecionarData from '@/core/components/UI/SelecionarData.vue';
import ServicoUsuario from '@/servicos/ServicoUsuario';
import { EStatusResposta, IResposta } from '@/core/models/IResposta';
import listaPadraoprivilegiosUsuario, { IDTOUsuarioPrivilegio } from '@/models/DTO/Usuarios/IDTOUsuarioPrivilegio';
import UtilitarioGeral from '@/core/utilitarios/UtilitarioGeral';
import storeApp from '@/store/storeApp';
import { ETipoIntegracao } from '@/models/Enumeradores/ETipoIntegracao';
import { IIntegracao } from '@/models/Entidades/IIntegracao';
import { IVinculo } from '@/models/Entidades/IVinculo';
import { ETipoVinculo } from '@/models/Enumeradores/ETipoVinculo';
import { IDTOBuscaVendedor } from '@/models/DTO/IDTOBuscaVendedor';
import { IDTOCadastroPadraoString } from '@/models/DTO/IDTOCadastroPadraoString';
import ServicoApp from '@/servicos/ServicoApp';
import UtilitarioData from '@/core/utilitarios/UtilitarioData';

export default defineComponent({
  name: 'UsuarioCadastro',
  components: {
    Card,
    Icone,
    MensagemSemDados,
    ComunicacaoApi,
    CampoEmail,
    CampoTelefone,
    CampoSenha,
    SelecionarSimNao,
    SelecionarData,
  },
  setup() {
    const {
      appBase, apresentarComunicacaoApi, apresentarRespostaRedirecionamento, obterCodigoRota, apresentarResposta,
    } = useAppBase();
    const servicoUsuario = new ServicoUsuario();
    const servicoApp = new ServicoApp();

    let debounce = 0;

    const state = reactive({
      usuario: {} as IUsuario,
      integracaoErp: {} as IIntegracao,
      vinculos: [] as IVinculo[],
      privilegios: [] as IDTOUsuarioPrivilegio[],
      apresentarSelecaoVendedores: false,
      buscaVendedor: {} as IDTOBuscaVendedor,
      vendedoresErp: [] as IDTOCadastroPadraoString[],
      buscandoVendedores: false,
    });

    function verificaIntegracaoErpVendedor(): boolean {
      if (UtilitarioGeral.validaLista(storeApp.state.integracoesErp)) {
        if (storeApp.state.integracoesErp[0].codigo > 0 && storeApp.state.integracoesErp[0].tipo === ETipoIntegracao.ErpVisualSAEF) {
          state.integracaoErp = storeApp.state.integracoesErp[0] as IIntegracao;
          return true;
        }
      }

      return false;
    }

    async function obterUsuario(codigo:number) {
      appBase.carregando = true;
      state.usuario = await servicoUsuario.obter(codigo);
      if (UtilitarioGeral.validaLista(state.usuario.privilegios)) {
        state.usuario.privilegios.forEach((usuarioPrivilegio) => {
          const index = state.privilegios.findIndex((c) => c.identificador === usuarioPrivilegio.privilegio);
          if (index >= 0) {
            state.privilegios[index].valor = true;
          }
        });
      }
      if (verificaIntegracaoErpVendedor()) {
        state.vinculos = await servicoUsuario.obterVinculos(codigo);
      }

      appBase.carregando = false;
    }

    function limparDados() {
      state.buscandoVendedores = false;
      state.apresentarSelecaoVendedores = false;
      state.vendedoresErp = [];
      state.integracaoErp = {} as IIntegracao;
      state.vinculos = [];
      state.usuario = {} as IUsuario;
      state.usuario.dataCadastro = UtilitarioData.obterDataAtual();
      state.usuario.codigo = 0;
      state.usuario.status = true;
      state.usuario.confirmado = true;
      state.usuario.privilegios = [];
      state.privilegios = UtilitarioGeral.instanciaObjetoLocal(listaPadraoprivilegiosUsuario);
    }

    function buscarVendedores() {
      clearTimeout(debounce);
      debounce = setTimeout(async () => {
        state.buscandoVendedores = true;
        state.buscaVendedor.integracao = state.integracaoErp;
        state.vendedoresErp = [];
        const respostaConsulta = await servicoApp.buscarVendedores(state.buscaVendedor);
        if (UtilitarioGeral.validaLista(respostaConsulta)) {
          state.vendedoresErp = respostaConsulta;
        }
        state.buscandoVendedores = false;
      }, 600);
    }

    async function prepararBuscaVendedores() {
      state.buscaVendedor.nome = '';
      state.apresentarSelecaoVendedores = true;
      await buscarVendedores();
    }

    onBeforeMount(async () => {
      limparDados();
      const codigoUsuario = obterCodigoRota();
      if (codigoUsuario > 0) {
        await obterUsuario(codigoUsuario);
      }
    });

    function confirmarSelecaoVinculo(vendedorErp: IDTOCadastroPadraoString) {
      if (UtilitarioGeral.validaLista(state.vinculos)) {
        for (let index = 0; index < state.vinculos.length; index += 1) {
          if (state.vinculos[index].tipo === ETipoVinculo.UsuarioVendedor) {
            state.vinculos[index].codigoRelacionado = vendedorErp.codigo;
          } else if (state.vinculos[index].tipo === ETipoVinculo.UsuarioVendedorZona) {
            state.vinculos[index].codigoRelacionado = vendedorErp.informacaoAdicional;
          }
        }
      } else {
        const vinculo:IVinculo = {} as IVinculo;
        vinculo.codigo = 0;
        vinculo.codigoParceiro = 0;
        vinculo.tipo = ETipoVinculo.UsuarioVendedor;
        vinculo.codigoVinculo = state.usuario.codigo.toString();
        vinculo.codigoRelacionado = vendedorErp.codigo;
        state.vinculos.push(vinculo);

        const vinculoZona:IVinculo = {} as IVinculo;
        vinculoZona.codigo = 0;
        vinculoZona.codigoParceiro = 0;
        vinculoZona.tipo = ETipoVinculo.UsuarioVendedorZona;
        vinculoZona.codigoVinculo = state.usuario.codigo.toString();
        vinculoZona.codigoRelacionado = vendedorErp.informacaoAdicional;
        state.vinculos.push(vinculoZona);
      }

      if (!UtilitarioGeral.valorValido(state.usuario.nome)) {
        state.usuario.nome = vendedorErp.nome;
      }
      state.usuario.email = vendedorErp.codigo;
      state.apresentarSelecaoVendedores = false;
    }
    function preparaPrivilegios() {
      state.privilegios.forEach((privilegio) => {
        const indexPrivilegioExistente = state.usuario.privilegios.findIndex((c) => c.privilegio === privilegio.identificador);
        if (privilegio.valor) {
          if (indexPrivilegioExistente === -1) {
            const usuarioPrivilegio: IUsuarioPrivilegio = { codigo: 0, codigoUsuario: state.usuario.codigo, privilegio: privilegio.identificador };
            state.usuario.privilegios.push(usuarioPrivilegio);
          }
        } else if (!privilegio.valor) {
          if (indexPrivilegioExistente >= 0) {
            state.usuario.privilegios.splice(indexPrivilegioExistente, 1);
          }
        }
      });
    }
    async function salvar() {
      preparaPrivilegios();
      appBase.resposta = {} as IResposta;
      if (verificaIntegracaoErpVendedor() && !UtilitarioGeral.valorValido(state.usuario.telefone)) {
        state.usuario.telefone = '00000000000';
      }
      apresentarComunicacaoApi('Aguarde por favor... Estamos salvando as informações do usuário.');
      if (state.usuario.codigo === 0) {
        appBase.resposta = await servicoUsuario.incluir(state.usuario);
        if (appBase.resposta.status === EStatusResposta.Sucesso) {
          state.usuario.codigo = appBase.resposta.codigo;
        }
      } else {
        appBase.resposta = await servicoUsuario.atualizar(state.usuario);
      }

      if (verificaIntegracaoErpVendedor() && UtilitarioGeral.validaLista(state.vinculos)) {
        await servicoUsuario.atualizarVinculos(state.usuario.codigo, state.vinculos);
      }

      apresentarRespostaRedirecionamento('Usuarios');
    }

    async function removerVinculos() {
      if (state.usuario.codigo > 0) {
        appBase.resposta = {} as IResposta;
        apresentarComunicacaoApi('Aguarde por favor... Estamos removendo os vínculos do usuário.');
        appBase.resposta = await servicoUsuario.deletarVinculos(state.usuario.codigo);
        if (appBase.resposta.status === EStatusResposta.Sucesso) {
          state.vinculos = [];
        }
        apresentarResposta();
      } else {
        state.vinculos = [];
      }
    }
    return {
      appBase,
      state,
      ETipoVinculo,
      salvar,
      verificaIntegracaoErpVendedor,
      removerVinculos,
      buscarVendedores,
      prepararBuscaVendedores,
      confirmarSelecaoVinculo,
      UtilitarioGeral,
    };
  },
});
